<script setup lang="ts">
import {
  GpMenu,
  GpMenuItemAction,
  GpMenuItemContent,
  GpMenuItemDivider,
  GpMenuItemStatic,
} from '@gportal/uikit';
import { computed, ref } from 'vue';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { ProjectMemberRole } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const projectStore = useProjectStore();
const projectId = computed(() => projectStore.projectId);

const project = computed(() => projectStore.project);
const membership = computed(() => projectStore.currentUserMembership);
const isProjectAdmin = computed(
  () => projectStore.currentUserMembership?.role === ProjectMemberRole.OWNER,
);
const projectUsage = computed(() => projectStore.projectUsage);
const projectHasBilling = computed(() => projectStore.hasBillingProfile);
const projectNeedsBillingProfile = computed(() => {
  return (
    project.value &&
    membership.value &&
    membership.value.role === ProjectMemberRole.OWNER &&
    !projectHasBilling.value
  );
});

const projectMenu = ref<InstanceType<typeof GpMenu>>();
const navigateViaMenu = ($event, routerNavigateFn) => {
  $event.preventDefault();
  projectMenu.value.hideMenuBody();

  routerNavigateFn();
};
</script>

<template>
  <GpMenu
    ref="projectMenu"
    size="sm"
    class="tw-mr-4"
    label="Project"
    :btn-type="projectNeedsBillingProfile ? 'warning' : 'primary'"
    :icon="projectNeedsBillingProfile ? ['fas', 'fa-triangle-exclamation'] : null"
  >
    <template v-if="isProjectAdmin && projectUsage && projectUsage.total">
      <GpMenuItemStatic v-slot="{ contentClass }">
        <div :class="[contentClass, 'tw-block md:tw-hidden']">
          <p class="tw-text-xs tw-font-bold tw-uppercase tw-text-gp-rhino">Project usage</p>
          {{ projectUsage.total.formatted }}
        </div>
      </GpMenuItemStatic>
      <GpMenuItemDivider class="tw-block tw-border-gp-rhino md:tw-hidden" />
    </template>
    <GpMenuItemAction v-if="projectNeedsBillingProfile">
      <router-link
        :to="{ name: 'ProjectSettings', hash: '#billing', params: { projectId } }"
        v-slot="{ href, navigate }"
        custom
      >
        <GpMenuItemContent
          component="a"
          :href="href"
          class="tw-bg-gp-orange tw-px-4 tw-py-1 !tw-text-black hover:tw-bg-gp-orange-light"
          @click="navigateViaMenu($event, navigate)"
        >
          <FontAwesomeIcon
            :icon="['fas', 'fa-credit-card']"
            :href="href"
            class="tw-text-gp-black"
          />

          Setup Billing
        </GpMenuItemContent>
      </router-link>
    </GpMenuItemAction>
    <GpMenuItemAction v-slot="{ contentClass }">
      <router-link
        :to="{
          name: 'ProjectNodeCreate',
          params: {
            projectId,
          },
        }"
        custom
        v-slot="{ href, navigate }"
      >
        <GpMenuItemContent
          component="a"
          :icon="['fas', 'server']"
          label="Setup new node"
          :class="contentClass"
          :href="href"
          @click="($event) => navigateViaMenu($event, navigate)"
        />
      </router-link>
    </GpMenuItemAction>
  </GpMenu>
</template>

<style scoped></style>
